import { ElTable, ElMessageBox, ElMessage } from 'element-plus';
import { ref, reactive, watch, computed, unref, nextTick } from 'vue';
import { GbTables, TableExpose } from '@/components/GbTables';
import { TableSetPropsType, TableProps, TableObject } from '@/components/GbTables/GbTablesTypes';

interface TableResponse<T = any> {
	total: number;
	data: T[];
}

interface UseTableConfig<T = any> {
	defaultParams?: Recordable;
	props?: TableProps;
	response: {
		data: string;
		total?: string;
	};
	getListApi: (option: any) => Promise<IResponse<TableResponse<T>>>;
	delListApi?: (option: any) => Promise<IResponse>;
}

export const useGbTable = <T = any>(config?: UseTableConfig<T>) => {
	const tableRef = ref<typeof GbTables & TableExpose>();
	const elTableRef = ref<ComponentRef<typeof ElTable>>();

	const tableObject = reactive<TableObject<T>>({
		pageSize: 10,
		currentPage: 1,
		total: 0,
		tableList: [],
		loading: true,
		currentRow: null,
		params: {
			...(config?.defaultParams || {}),
		},
	});

	/* 查询参数 */
	const paramsObj = computed(() => {
		return {
			...tableObject.params,
			page_size: tableObject.pageSize,
			current: tableObject.currentPage,
		};
	});

	const register = (ref: typeof GbTables & TableExpose, elRef: ComponentRef<typeof ElTable>) => {
		tableRef.value = ref;
		elTableRef.value = unref(elRef);
	};

	const getTable = async () => {
		await nextTick();

		const table = unref(tableRef);

		if (!table) {
			console.error('The table is not registered. Please use the register method to register');
		}

		return table;
	};

	const delDataRow = async (ids: string | number | any) => {
		const res = await (config?.delListApi && config?.delListApi(ids));
		if (res?.succeeded) {
			ElMessage.success('删除成功');

			const currentPage = tableObject.total % tableObject.pageSize === 1 || tableObject.pageSize === 1 ? (tableObject.currentPage > 1 ? tableObject.currentPage - 1 : tableObject.currentPage) : tableObject.currentPage;

			tableObject.currentPage = currentPage;

			methods.getList();
		} else {
			ElMessage.error(`删除失败，可能的原因是：${res?.errors}`);
		}
	};

	const methods = {
		setSearchParams: (data: Recordable) => {
			tableObject.currentPage = 1;

			tableObject.params = Object.assign(tableObject.params, {
				page_size: tableObject.pageSize,
				current: tableObject.currentPage,
				...data,
			});

			methods.getList();
		},

		getListNoPaged: async (data: any) => {
			// tableObject.params = data

			tableObject.loading = true;

			const res = await config?.getListApi(data).finally(() => {
				tableObject.loading = false;
			});

			if (!res?.succeeded) {
				ElMessage.error(`读取数据失败，可能的原因时：${res?.errors}`);
			} else {
				tableObject.tableList = (res.data.data || res.data || []) as [];
				tableObject.total = res.data.total || 0; // et(res.data.total || {}, config?.response?.total as string) || 0
			}
		},

		/* 获取数据列表 */
		getList: async () => {
			tableObject.loading = true;

			const res = await config?.getListApi(unref(paramsObj)).finally(() => {
				tableObject.loading = false;
			});

			if (!res?.succeeded) {
				ElMessage.error(`读取列表失败，可能的原因是：${res?.errors}`);
			} else {
				tableObject.tableList = (res.data.data || res.data) as [];

				tableObject.total = res.data.total;
			}
		},

		resetParams: (data: any) => {
			tableObject.params = Object.assign({}, { ...data, current: 1, page_size: 10 });
			methods.getList();
		},

		/* 删除某行记录 */
		deleteRows: async (row: string | number | any, showMessage = true) => {
			if (showMessage) {
				ElMessageBox.confirm('是否删除选中的数据？', '提示', { lockScroll: false, confirmButtonText: '确定', cancelButtonText: '取消', type: 'warning' }).then(async () => {
					await delDataRow(row);
				});
			} else {
				await delDataRow(row);
			}
		},

		setProps: async (props: TableProps = {}) => {
			const table = await getTable();
			table?.setProps(props);
		},

		setColumn: async (columnProps: TableSetPropsType[]) => {
			const table = await getTable();
			table?.setColumn(columnProps);
		},

		getSelections: async () => {
			const table = await getTable();
			return (table?.selections || []) as T[];
		},
	};

	watch(
		() => tableObject.currentPage,
		() => {
			methods.getList();
		}
	);

	watch(
		() => tableObject.pageSize,
		() => {
			if (tableObject.currentPage === 1) {
				methods.getList();
			} else {
				tableObject.currentPage = 1;
				methods.getList();
			}
		}
	);

	config?.props && methods.setProps(config.props);
	return { elTableRef, tableObject, methods, register };
};
